import JSZip from "jszip";
import { v4 as uuidv4 } from "uuid";
import { openZipPresentationFromBlob, MAX_ZIP_BYTES_FOR_INLINE_PLAYER } from "./ispringZipPresentation";

const SW_SCRIPT = "/ima-lesson-sw.js";
const URL_PREFIX = "/__ima-lesson/";
/** Trùng public/ima-lesson-sw.js */
const IDB_NAME = "ima_edu_lesson_sw_pack_v2";
const IDB_VER = 1;
const IDB_STORE = "files";

const SESSION_TTL_MS = 2 * 60 * 60 * 1000;
const sessionCleanupTimers = new Map();
/** Gửi từng lô sang SW để tránh vượt giới hạn postMessage / treo main thread. */
const SW_STORE_CHUNK_SIZE = 40;

function normPath(p) {
  return String(p || "")
    .replace(/\\/g, "/")
    .replace(/^(\.\/)+/, "");
}

/** Đường dẫn tương đối thống nhất với public/ima-lesson-sw.js (IDB key + URL). */
function canonicalLessonRelPath(p) {
  return normPath(p).split("/").filter(Boolean).join("/").toLowerCase();
}

function mimeFromPath(p) {
  const ext = (p.split(".").pop() || "").toLowerCase();
  const map = {
    html: "text/html",
    htm: "text/html",
    js: "application/javascript",
    mjs: "application/javascript",
    css: "text/css",
    json: "application/json",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    mp4: "video/mp4",
    webm: "video/webm",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    woff: "font/woff",
    woff2: "font/woff2",
    ttf: "font/ttf",
    otf: "font/otf",
    wasm: "application/wasm",
    xml: "application/xml",
    swf: "application/x-shockwave-flash",
  };
  return map[ext] || "application/octet-stream";
}

function findZipHtmlIndexPath(paths) {
  let indexPath = paths.find((p) => /(^|\/)res\/index\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)res\/index\.htm$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)index\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)index\.htm$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)story\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => p.toLowerCase().endsWith(".html"));
  return indexPath || null;
}

function assetUrl(origin, sessionId, zipPath) {
  const canon = canonicalLessonRelPath(zipPath);
  const parts = canon.split("/").map(encodeURIComponent);
  return `${origin}${URL_PREFIX}${sessionId}/${parts.join("/")}`;
}

function openLessonPackDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function writeLessonRowsViaServiceWorker(sessionId, rows) {
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    throw new Error("Không có Service Worker đang điều khiển trang.");
  }
  for (let i = 0; i < rows.length; i += SW_STORE_CHUNK_SIZE) {
    const chunk = rows.slice(i, i + SW_STORE_CHUNK_SIZE);
    const channel = new MessageChannel();
    const done = new Promise((resolve, reject) => {
      channel.port1.onmessage = (e) => {
        if (e.data?.ok) resolve(e.data);
        else reject(new Error(e.data?.error || "Ghi phiên bài giảng thất bại"));
      };
    });
    controller.postMessage({ type: "IMA_LESSON_STORE", sessionId, rows: chunk }, [channel.port2]);
    const timeoutMs = i === 0 ? 8000 : 120000;
    await Promise.race([
      done,
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Hết thời gian chờ Service Worker ghi dữ liệu (IndexedDB).")),
          timeoutMs
        )
      ),
    ]);
  }
}

async function writeLessonRowsViaPageTransaction(rows) {
  const db = await openLessonPackDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    const os = tx.objectStore(IDB_STORE);
    for (const row of rows) {
      os.put({ mime: row.mime, body: row.body }, row.key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction abort"));
  });
}

/**
 * Ghi toàn bộ file ZIP — ưu tiên ghi trong Service Worker (cùng ngữ cảnh với fetch handler),
 * fallback ghi từ trang nếu SW chưa hỗ trợ hoặc lỗi.
 */
async function writeLessonSessionToIdb(sessionId, zip, paths) {
  const rows = [];
  for (const path of paths) {
    const entry = zip.file(path);
    if (!entry) continue;
    const body = await entry.async("arraybuffer");
    const key = `${sessionId}/${canonicalLessonRelPath(path)}`;
    rows.push({ key, mime: mimeFromPath(path), body });
  }
  if (rows.length === 0) {
    throw new Error("Không có file nào trong ZIP để lưu.");
  }

  try {
    await writeLessonRowsViaServiceWorker(sessionId, rows);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[IMA] Lesson IDB: ghi qua SW thất bại, thử ghi từ trang:", e);
    await writeLessonRowsViaPageTransaction(rows);
  }

  return rows.length;
}

async function deleteLessonSessionFromIdb(sessionId) {
  const controller = navigator.serviceWorker.controller;
  if (controller) {
    try {
      const channel = new MessageChannel();
      const done = new Promise((resolve, reject) => {
        channel.port1.onmessage = (e) => {
          if (e.data?.ok) resolve();
          else reject(new Error(e.data?.error || "Xóa phiên thất bại"));
        };
      });
      controller.postMessage({ type: "IMA_LESSON_DELETE", sessionId }, [channel.port2]);
      await Promise.race([
        done,
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 15000)),
      ]);
      return;
    } catch {
      /* fallback xóa từ trang */
    }
  }

  const prefix = `${sessionId}/`;
  const db = await openLessonPackDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    const os = tx.objectStore(IDB_STORE);
    const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`, false, false);
    const cur = os.openCursor(range);
    cur.onsuccess = () => {
      const c = cur.result;
      if (c) {
        c.delete();
        c.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function scheduleSessionCleanup(sessionId) {
  const prev = sessionCleanupTimers.get(sessionId);
  if (prev) window.clearTimeout(prev);
  const t = window.setTimeout(() => {
    sessionCleanupTimers.delete(sessionId);
    deleteLessonSessionFromIdb(sessionId).catch(() => {});
  }, SESSION_TTL_MS);
  sessionCleanupTimers.set(sessionId, t);
}

async function ensureLessonServiceWorker() {
  if (!("serviceWorker" in navigator) || !("indexedDB" in window)) {
    throw new Error("Trình duyệt không hỗ trợ Service Worker hoặc IndexedDB.");
  }
  const registration = await navigator.serviceWorker.register(SW_SCRIPT, { scope: "/" });
  await navigator.serviceWorker.ready;

  const postSkipWaiting = () => {
    try {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    } catch {
      /* ignore */
    }
  };
  postSkipWaiting();

  if (navigator.serviceWorker.controller) {
    return registration;
  }

  /**
   * Lần đầu cài SW, clients.claim() có thể chậm hơn 8s — không có controller
   * thì luồng chuyển sang blob và không ghi IndexedDB (store files rỗng).
   */
  await new Promise((resolve) => {
    let finished = false;
    const maxMs = 20000;
    const deadline = Date.now() + maxMs;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      clearInterval(tick);
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve();
    };
    const onChange = () => {
      postSkipWaiting();
      if (navigator.serviceWorker.controller) {
        cleanup();
      }
    };
    const tick = setInterval(() => {
      if (navigator.serviceWorker.controller) {
        cleanup();
      } else if (Date.now() > deadline) {
        cleanup();
      } else {
        postSkipWaiting();
      }
    }, 150);
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
  });

  return registration;
}

/**
 * Mở bài giảng ZIP đã có trên máy (buffer giải mã) qua URL gốc app + Service Worker.
 * Dữ liệu phiên lưu IndexedDB (SW đọc) — không dùng Cache API cho từng file.
 *
 * @param {ArrayBuffer | Blob} zipBytes
 * @param {{ fallbackToBlob?: boolean; preOpenedWindow?: Window | null }} [options]
 *   preOpenedWindow: cửa sổ mở sẵn (about:blank) trong cùng lần click — tránh popup bị chặn sau await.
 */
export async function openCachedLessonZipViaServiceWorker(zipBytes, options = {}) {
  const { fallbackToBlob = true, preOpenedWindow = null } = options;
  const buf =
    zipBytes instanceof Blob ? await zipBytes.arrayBuffer() : zipBytes;
  const size = buf.byteLength ?? 0;
  if (size > MAX_ZIP_BYTES_FOR_INLINE_PLAYER) {
    throw new Error(
      `Gói ZIP quá lớn (${Math.round(size / (1024 * 1024))} MB) để mở trong trình duyệt.`
    );
  }

  try {
    await ensureLessonServiceWorker();
  } catch (e) {
    if (fallbackToBlob) {
      await openZipPresentationFromBlob(new Blob([buf], { type: "application/zip" }), {
        preOpenedWindow,
      });
      return { mode: "blob-fallback", error: String(e?.message || e) };
    }
    throw e;
  }

  if (!navigator.serviceWorker.controller) {
    if (fallbackToBlob) {
      await openZipPresentationFromBlob(new Blob([buf], { type: "application/zip" }), {
        preOpenedWindow,
      });
      return { mode: "blob-fallback", reason: "no-service-worker-controller" };
    }
    throw new Error(
      "Service Worker chưa kích hoạt trên trang này. Hãy tải lại trang (F5) rồi thử «Trình chiếu» lại."
    );
  }

  const zip = await JSZip.loadAsync(buf);
  const paths = Object.keys(zip.files).filter((p) => !zip.files[p].dir);
  const indexPath = findZipHtmlIndexPath(paths);
  if (!indexPath) {
    throw new Error("Không tìm thấy file HTML trong ZIP.");
  }

  const sessionId = uuidv4();
  const origin = window.location.origin;

  try {
    const n = await writeLessonSessionToIdb(sessionId, zip, paths);
    if (n < paths.length) {
      // eslint-disable-next-line no-console
      console.warn(`[IMA] Lesson IDB: đã lưu ${n}/${paths.length} file.`);
    }
  } catch (e) {
    await deleteLessonSessionFromIdb(sessionId).catch(() => {});
    if (fallbackToBlob) {
      await openZipPresentationFromBlob(new Blob([buf], { type: "application/zip" }), {
        preOpenedWindow,
      });
      return { mode: "blob-fallback", error: String(e?.message || e) };
    }
    throw e;
  }

  if (preOpenedWindow && preOpenedWindow.closed) {
    const tm = sessionCleanupTimers.get(sessionId);
    if (tm) window.clearTimeout(tm);
    sessionCleanupTimers.delete(sessionId);
    await deleteLessonSessionFromIdb(sessionId).catch(() => {});
    throw new Error("Cửa sổ trình chiếu đã bị đóng trước khi tải xong.");
  }

  scheduleSessionCleanup(sessionId);

  const openUrl = assetUrl(origin, sessionId, indexPath);
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.location.replace(openUrl);
  } else {
    const win = window.open(openUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      const tm = sessionCleanupTimers.get(sessionId);
      if (tm) window.clearTimeout(tm);
      sessionCleanupTimers.delete(sessionId);
      await deleteLessonSessionFromIdb(sessionId).catch(() => {});
      throw new Error("Trình duyệt đã chặn cửa sổ mới. Vui lòng cho phép popup.");
    }
  }

  return { mode: "service-worker", openUrl, sessionId };
}
